import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { MandatorModel } from 'src/app/models/management/mandator.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MandatorTableModel } from 'src/app/models/tables/management/mandator-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { MandatorService } from 'src/app/services/management/mandator.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-mandator',
  templateUrl: './mandator.component.html',
  styleUrls: ['./mandator.component.scss'],
  standalone: false
})
export class MandatorComponent implements OnInit {

  ascendant: boolean = true;
  mandatorContract: MandatorModel = new MandatorModel();
  mandatorId: number = 0;
  columnSort: string | null = null;
  companyId: number = 0;
  disabled: boolean = false;
  data: PagedResponseModel<MandatorTableModel> = new PagedResponseModel<MandatorTableModel>();
  formMandator: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  input$: BehaviorSubject<any>;
  limit: number = 10;
  listTypeDocuments: CustomListDropdown[] = [];
  page: number = 1;
  suscription: Subscription;
  totalPercentage: number = 0;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;

  constructor(private confirmDialogService: ConfirmDialogService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private mandatorService: MandatorService,
    private toastHandlerService: ToastHandlerService,
    private companyService: CompanyService,
    @Optional() private ref: DynamicDialogRef,) {
    this.validateForm(new MandatorModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.companyId = inputs.companyId;
      this.disabled = inputs.disabledForm;
    });
    await Promise.all([
      this.getAllLists(),
      this.getColumnsForSelection(),
      this.getMandatorsForSelection()
    ]);
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async createMandator(model: MandatorModel) {
    try {
      await firstValueFrom(this.mandatorService.createMandator(model));
      this.validateForm(new MandatorModel());
      this.toastHandlerService.showToast('success', `El mandante fue creada exitosamente.`);
    } catch (error) {
      console.error('Error al crear el mandante.', error);
    }
  }

  async deleteSomeMandators(selectedIds: number[]) {
    if (selectedIds.length > 0) {
      if (await this.confirmDialogService.confirm("¿Realmente desea eliminar el/los mandantes seleccionados?")) {
        try {
          await firstValueFrom(this.mandatorService.deleteSomeMandators(selectedIds));
          this.toastHandlerService.showToast('success', 'El/los mandantes fueron eliminados correctamente.');
          this.clearSelectionTrigger++;
          await this.getMandatorsForSelection();
        } catch (error) {
          console.error('Error al eliminar el/los mandantes.', error);
        }
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.mandatorService.exportGeneralConsultation(
        this.columnSort, this.ascendant, this.companyId
      ));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Mandantes.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar mandantes.', error);
    }
  }

  async getAllLists() {
    try {
      const response = await firstValueFrom(this.companyService.getAllTypeDocumentsForSelect());
      this.listTypeDocuments = response;
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForSelection() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.mandatorService.getColumnsForSelection());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getDigitVerification(document: string) {
    if (!document) {
      this.formMandator.controls["digitVerification"].setValue(-1);
      this.toastHandlerService.showToast('error', 'El documento ingresado no es válido.');
    } else {
      try {
        const response = await firstValueFrom(this.companyService.getDigitVerification(document));
        this.formMandator.controls["digitVerification"].setValue(response);
      } catch (error) {
        this.formMandator.controls["digitVerification"].setValue(-1);
        console.error('Error al obtnener el digito de verification.', error);
      }
    }
  }

  async getMandatorById(mandatorId: number) {
    try {
      const response = await firstValueFrom(this.mandatorService.getMandatorById(mandatorId));
      this.mandatorContract = response;
      this.mandatorId = response.mandatorId;
      this.validateForm(this.mandatorContract);
    } catch (error) {
      console.error('Error al buscar los mandantes.', error);
    }
  }

  async getMandatorsForSelection() {
    try {
      this.data = await firstValueFrom(this.mandatorService.getMandatorsForSelection(this.page,
        this.limit, this.columnSort, this.ascendant, this.companyId));
      this.totalPercentage = this.data.items.reduce((sum, detail) => sum + (detail.percentage ?? 0), 0);
    } catch (error) {
      console.error('Error al buscar los mandantes.', error);
    }
  }

  async newMandator() {
    this.mandatorContract = new MandatorModel();
    this.validateForm(this.mandatorContract);
  }

  async onAction(event: { action: string, item?: MandatorTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      await this.getMandatorById(event.item.id);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      await this.deleteSomeMandators(event.selectedItems);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getMandatorsForSelection();
  }

  async send() {
    this.validateFormControls();
    if (!this.formMandator.valid) {
      return this.showValidationMessages();
    }
    const modelMandator = this.formMandator.getRawValue() as MandatorModel;
    modelMandator.companyId = this.companyId;
    if (!this.validateTotalPercentage(modelMandator)) {
      return this.toastHandlerService.showToast('error', 'El porcentaje total de los mandantes no puede superar el 100%.');
    }
    if (this.mandatorId === 0) {
      await this.createMandator(modelMandator);
    } else {
      await this.UpdateMandator(modelMandator);
    }
    await this.getMandatorsForSelection();
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      typeDocumentId: {
        required: 'El campo Tipo de documento es obligatorio.',
        min: 'Debe seleccionar un Tipo de documento válido.'
      },
      document: {
        required: 'El campo Documento es obligatorio.',
        maxlength: 'El campo Documento no puede tener más de 15 caracteres.'
      },
      digitVerification: {
        required: 'El campo Dígito de verificación es obligatorio.',
        min: 'El Dígito de verificación debe estar entre 0 y 9.',
        max: 'El Dígito de verificación debe estar entre 0 y 9.'
      },
      businessName: {
        required: 'El campo Razón social es obligatorio.',
        maxlength: 'El campo Razón social no puede tener más de 150 caracteres.'
      },
      percentage: {
        min: 'El Porcentaje no puede ser menor a 0.',
        max: 'El Porcentaje no puede ser mayor a 100.'
      }
    };
    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formMandator.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async UpdateMandator(model: MandatorModel) {
    this.mandatorContract.typeDocumentId = model.typeDocumentId;
    this.mandatorContract.document = model.document;
    this.mandatorContract.digitVerification = model.digitVerification;
    this.mandatorContract.businessName = model.businessName;
    this.mandatorContract.percentage = model.percentage;
    try {
      await firstValueFrom(this.mandatorService.UpdateMandator(this.mandatorContract));
      this.validateForm(new MandatorModel());
      this.toastHandlerService.showToast('success', `el mandante modificado exitosamente.`);
      this.mandatorId = 0;
    } catch (error) {
      console.error('Error al modificar el mandante.', error);
    }
  }

  validateForm(mandator: MandatorModel) {
    setTimeout(() => this.formMandator.get('digitVerification')?.disable());
    this.formMandator = this.formBuilder.group({
      typeDocumentId: [mandator.typeDocumentId, [Validators.required, Validators.min(1)]],
      document: [mandator.document, [Validators.required, Validators.maxLength(15)]],
      digitVerification: [mandator.digitVerification, [Validators.required, Validators.min(0),
      Validators.max(9)]],
      businessName: [mandator.businessName, [Validators.required, Validators.maxLength(150)]],
      percentage: [mandator.percentage, [Validators.min(0), Validators.max(100)]],
    });
  }

  private validateTotalPercentage(model: MandatorModel): boolean {
    let currentTotal = this.totalPercentage ?? 0;
    const newPercentage = model.percentage ?? 0;
    //Si es edición, restamos el porcentaje actual del registro:
    if (this.mandatorId) {
      const existing = this.data?.items?.find(x => x.id === this.mandatorId);
      currentTotal -= existing?.percentage ?? 0;
    }
    return currentTotal + newPercentage <= 100;
  }

  validateFormControls() {
    Object.keys(this.formMandator.controls).forEach((key) => {
      const control = this.formMandator.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
